import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing authentication on app load
    const checkAuth = () => {
      const token = localStorage.getItem('auth_token');
      const username = localStorage.getItem('username');
      const userRole = localStorage.getItem('user_role');

      console.log('Auth check:', { token: !!token, username, userRole });

      if (token && username) {
        setUser({
          username,
          role: userRole || 'user',
          token
        });
        console.log('User authenticated:', username);
      } else {
        setUser(null);
        console.log('No authentication found');
      }
      setLoading(false);
    };

    checkAuth();

    // Listen for storage changes (in case auth data is updated in another tab)
    const handleStorageChange = (e) => {
      if (e.key === 'auth_token' || e.key === 'username' || e.key === 'user_role') {
        checkAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const signIn = async (username, password) => {
    try {
      console.log('Starting sign in process for:', username);

      // Call Supabase RPC auth function directly
      const { data, error } = await supabase.rpc('auth', {
        p_username: username,
        p_password: password
      });

      console.log('Supabase RPC auth response:', { data, error });

      if (error) {
        console.error('RPC auth error:', error);
        return { error: { message: error.message || 'Authentication failed' } };
      }

      if (!data || !data.success) {
        console.log('Authentication failed:', data?.message);
        return { error: { message: data?.message || 'Invalid credentials' } };
      }

      console.log('Authentication successful, storing tokens...');

      // Generate JWT token for the session
      const token = btoa(JSON.stringify({
        username: data.user.USERNAME,
        userId: data.user.ID,
        role: data.user.USER_ROLE,
        timestamp: Date.now()
      }));

      // Store authentication data
      localStorage.setItem('auth_token', token);
      localStorage.setItem('username', data.user.USERNAME);
      localStorage.setItem('user_role', data.user.USER_ROLE || 'user');
      localStorage.setItem('user_id', data.user.ID);

      if (data.user.EMAIL) {
        localStorage.setItem('user_email', data.user.EMAIL);
      }

      // Generate session key for security
      const timestamp = new Date().getTime();
      const randomStr = Math.random().toString(36).substring(2, 15);
      const sessionHash = `${data.user.USERNAME}-${timestamp}-${randomStr}`;
      localStorage.setItem('session_key', sessionHash);
      localStorage.setItem('session_created', timestamp);

      console.log('Setting user in context...');

      // Set user in context
      setUser({
        id: data.user.ID,
        username: data.user.USERNAME,
        email: data.user.EMAIL,
        role: data.user.USER_ROLE || 'user',
        token: token
      });

      // Small delay to ensure state is properly set
      await new Promise(resolve => setTimeout(resolve, 100));

      console.log('Sign in process completed successfully');

      return { data: { user: data.user, token } };
    } catch (error) {
      console.error('Sign in error:', error);
      return { error: { message: 'Network error or unexpected issue. Please try again.' } };
    }
  };

  const signOut = async () => {
    // Clear authentication data
    const keysToRemove = ['auth_token', 'username', 'user_role', 'client_id', 'session_key', 'session_created'];
    keysToRemove.forEach(key => localStorage.removeItem(key));

    setUser(null);
    return { error: null };
  };

  const value = {
    user,
    loading,
    signIn,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};